import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { PhoneInput } from "@freightsimple/ui-core/Controls/PhoneInput";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { TextInput } from "@freightsimple/ui-core/Controls/TextInput";
import { NormalQuestionBubble } from "@/Shared/Components/QuestionBubble";
import type { DialogBaseProps } from "@freightsimple/ui-core/Dialog/DialogBaseProps";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { Switch } from "@freightsimple/ui-core/Controls/Switch";
import { toast } from "sonner";
import {
  errorMessageForContactName,
  errorMessageForPhoneNumber,
} from "@/Shared/Contacts/Validators/contactsErrorMessagesForContact";
import { useUsersApi } from "@/Hooks/useUsersApi";
import { refusalMessage } from "@/Helpers/refusalMessage";
import type { User } from "@freightsimple/generated-dashboard-openapi-client/models/User";
import { ResponseError } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import { useState } from "react";

export interface ManageUserDialogDoneData {
  updatedUser: User;
  type: "delete" | "update";
}

interface ViewCompanyManageUserDialogProps extends DialogBaseProps {
  user: User;
  /** Fired after a save or delete has been persisted. */
  onFinished: (data: ManageUserDialogDoneData) => void;
  /**
   * Why this user can't be deleted, or undefined when deleting is allowed. The
   * delete button explains itself with this instead of firing a call the
   * backend will refuse.
   */
  deletionBlockedReason: string | undefined;
}

// Manage a team member: contact details, notification preferences, and
// permanent deletion (with a stacked confirm). Replaces the legacy
// imperative modal.
export function ViewCompanyManageUserDialog(
  props: ViewCompanyManageUserDialogProps,
) {
  const [loading, setLoading] = useState(false);

  const [userName, setUserName] = useState<string>(props.user.name);
  const [userPhone, setUserPhone] = useState<string>(props.user.phone);
  const [userPhoneExtension, setUserPhoneExtension] = useState<string>(
    props.user.phoneExtension,
  );
  const [accountingNotifications, setAccountingNotifications] = useState(
    props.user.accountingNotificationsEnabled,
  );
  const [allShipmentNotifications, setAllShipmentNotifications] = useState(
    props.user.allShipmentNotificationsEnabled,
  );
  const [deliveryNotificationsOnly, setDeliveryNotificationsOnly] = useState(
    props.user.deliveryNotificationsOnlyEnabled,
  );
  const [forceValidation, setForceValidation] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const userNameErrorMessage = errorMessageForContactName(userName);
  const phoneNumberErrorMessage = errorMessageForPhoneNumber(userPhone);

  const userApi = useUsersApi();

  const isValid =
    userNameErrorMessage === undefined && phoneNumberErrorMessage === undefined;

  async function onSave() {
    setForceValidation(true);

    if (!isValid) {
      return;
    }

    setLoading(true);

    const response = await userApi.update({
      updateUserRequest: {
        userId: props.user.userId,
        name: userName,
        phoneNumber: userPhone,
        phoneNumberExtension: userPhoneExtension,
        accountingNotificationsEnabled: accountingNotifications,
        allShipmentNotificationsEnabled: allShipmentNotifications,
        deliveryNotificationsOnlyEnabled: deliveryNotificationsOnly,
      },
    });

    setLoading(false);
    props.onOpenChange?.(false);
    props.onFinished({ updatedUser: response, type: "update" });
  }

  async function onDeleteConfirmed() {
    setConfirmDeleteOpen(false);
    setLoading(true);

    try {
      await userApi.deleteUser({
        deleteUserRequest: {
          userId: props.user.userId,
        },
      });
    } catch (e) {
      // The API refuses deleting your own user with a 409. The button is
      // disabled for that, so reaching it means the screen is out of date —
      // say so rather than leaving the dialog spinning.
      if (e instanceof ResponseError && e.response.status === 409) {
        toast.error(
          (await refusalMessage(e)) ?? "That user can no longer be deleted",
        );
      } else {
        console.error("Something went wrong deleting the user", { e });
        toast.error(
          "Something went wrong deleting the user. Please try again or ask us for help",
        );
      }
      setLoading(false);
      return;
    }

    setLoading(false);
    props.onOpenChange?.(false);
    props.onFinished({ updatedUser: props.user, type: "delete" });
  }

  return (
    <>
      <Dialog
        open={props.open}
        onOpenChange={props.onOpenChange}
        title="Manage User"
        description="You can update your team member's contact details and notification preferences here"
        width="narrow"
        footer={
          <Row gap={6}>
            <Button
              size="link"
              tone="danger"
              onClick={() => setConfirmDeleteOpen(true)}
              data-testid="manage-user-delete-button"
              {...(props.deletionBlockedReason === undefined
                ? {}
                : {
                    disabled: true,
                    disabledMessage: props.deletionBlockedReason,
                  })}
            >
              Permanently Delete User
            </Button>
            <Row gap={2}>
              <Button
                onClick={() => props.onOpenChange?.(false)}
                data-testid="manage-user-cancel-button"
              >
                Cancel
              </Button>
              <Button
                primary
                loading={loading}
                onClick={onSave}
                data-testid="manage-user-save-button"
              >
                Save Changes!
              </Button>
            </Row>
          </Row>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {/* Each field's bold caption is the FormItem's own emphasized label,
              so the hand-rolled label divs and their 5px spacers are gone. */}
          <Stack gap={2}>
            <ShowFormErrors show={forceValidation}>
              <FormItem
                label="Full Name"
                emphasizeLabel
                error={userNameErrorMessage}
                fillWidth
              >
                <TextInput
                  type="text"
                  placeholder="Enter full name"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  width="full"
                  data-testid="manage-user-name-input"
                />
              </FormItem>
            </ShowFormErrors>

            <ShowFormErrors show={forceValidation}>
              <FormItem
                label="Phone Number"
                emphasizeLabel
                error={phoneNumberErrorMessage}
                fillWidth
              >
                <PhoneInput
                  testId="manage-user-phone-input"
                  phoneNumber={userPhone}
                  extension={userPhoneExtension}
                  onPhoneNumberChange={(value: string) => {
                    setUserPhone(value);
                  }}
                  onExtensionChange={(value: string) => {
                    setUserPhoneExtension(value);
                  }}
                  width="full"
                />
              </FormItem>
            </ShowFormErrors>

            <FormItem
              emphasizeLabel
              label={
                <>
                  Accounting Notifications?{" "}
                  <NormalQuestionBubble
                    description="When enabled the user will receive notifications about
                            invoices and additional charges"
                  />
                </>
              }
            >
              <Switch
                checked={accountingNotifications}
                onChange={setAccountingNotifications}
                data-testid="manage-user-accounting-notifications-switch"
              />
            </FormItem>

            <FormItem
              emphasizeLabel
              label={
                <>
                  All Shipment Notifications?{" "}
                  <NormalQuestionBubble
                    description="When enabled the user will receive notifications about
                            booking and delivery of all shipments, regardless of who
                            booked"
                  />
                </>
              }
            >
              <Switch
                checked={allShipmentNotifications}
                onChange={(value) => {
                  setAllShipmentNotifications(value);
                  //Turn off delivery notifications only if all shipments notifications are enabled
                  //Doesn't make sense to have delivery notifications only
                  // if you're getting all shipment notifications
                  if (value) {
                    setDeliveryNotificationsOnly(false);
                  }
                }}
                data-testid="manage-user-all-shipment-notifications-switch"
              />
            </FormItem>

            <FormItem
              emphasizeLabel
              label={
                <>
                  Delivery Notifications Only?{" "}
                  <NormalQuestionBubble
                    description="When enabled the user will receive notifications only about the delivery of all shipments,
              regardless of who booked"
                  />
                </>
              }
            >
              <Switch
                checked={deliveryNotificationsOnly}
                onChange={(value) => {
                  setDeliveryNotificationsOnly(value);
                  //Turn off all shipment notifications if only delilvery notifications are enabled
                  //Doesn't make sense to have all shipment notifications
                  // if you're getting only delivery notifications
                  if (value) {
                    setAllShipmentNotifications(false);
                  }
                }}
                data-testid="manage-user-delivery-notifications-only-switch"
              />
            </FormItem>
          </Stack>
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={confirmDeleteOpen}
        onOpenChange={(open) => {
          if (!open) setConfirmDeleteOpen(false);
        }}
        title={`Confirm deleting user ${props.user.email}`}
        okText="Delete User"
        onOk={onDeleteConfirmed}
      >
        <ControlPresentationProvider presentation="underlined">
          Please confirm you want to delete this user. They will no longer be
          able to access your account.
        </ControlPresentationProvider>
      </Dialog>
    </>
  );
}
